import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ContentProvider, useContent } from "./context/ContentContext";
import CookieBanner from "./components/common/CookieBanner";
import ScrollToTop from "./components/common/ScrollToTop";
import PageTracker from "./components/common/PageTracker";
import SeasonalEffects from "./components/common/SeasonalEffects";
import { EasterEggProvider } from "./components/common/EasterEggProvider";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const CreditsPage = lazy(() => import("./pages/CreditsPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const GodModePage = lazy(() => import("./pages/GodModePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ComingSoonPage = lazy(() => import("./pages/ComingSoonPage"));
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Laden...</div>
  </div>
);

const queryClient = new QueryClient();

const AppRoutes = () => {
  const { content } = useContent();
  const siteMode = content.site_settings?.mode || 'live';

  // Show coming soon or maintenance for non-admin routes
  if (siteMode === 'coming_soon') {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<ComingSoonPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (siteMode === 'maintenance') {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<MaintenancePage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:postId" element={<BlogPostPage />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/god-mode" element={<GodModePage />} />
        <Route path="/seite/:slug" element={<DynamicPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ContentProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PageTracker />
          <SeasonalEffects />
          <EasterEggProvider>
            <AppRoutes />
          </EasterEggProvider>
        </BrowserRouter>
        <CookieBanner />
      </TooltipProvider>
    </ContentProvider>
  </QueryClientProvider>
);

export default App;
